import { getPartnerProxyId, getToken, hasRole } from '@/utils/KeycloakUtils'
import { type Document } from '@/types/postbox/postbox-service'
import { useHelpdeskStore } from '@/stores/helpdeskStore'

interface NavigatorIE extends Navigator {
  msSaveOrOpenBlob(blob: Blob, defaultName?: string): boolean
}

// Utility: forcePdfInNewTabIframe
function forcePdfInNewTabIframe(blob: Blob, fileName: string) {
  const blobURL = globalThis.URL.createObjectURL(blob)
  const safeFileName = fileName.replaceAll(/[<>"']/g, '') // simple escaping
  const html = `
    <!DOCTYPE html>
    <title>${safeFileName}</title>
    <style>
      body { margin: 0; padding: 0; height: 100svh;}
    </style>
    <iframe
      src="${blobURL}"
      width="100%"
      height="100%"
      style="border:none;display:block;"
    ></iframe>
  `
  const newTab = globalThis.open()
  if (!newTab) {
    alert(
      'Das Öffnen des Dokuments wurde durch einen Popup-Blocker verhindert. Bitte erlauben Sie Popups für diese Seite.'
    )
    globalThis.URL.revokeObjectURL(blobURL)
    return
  }
  const htmlBlob = new Blob([html], { type: 'text/html' })
  const htmlUrl = URL.createObjectURL(htmlBlob)
  newTab.location.href = htmlUrl
  // Optional: revoke after 2min
  setTimeout(() => {
    globalThis.URL.revokeObjectURL(blobURL)
    URL.revokeObjectURL(htmlUrl)
  }, 120000)
}

export const downloadFile = async (document: Document, openInNewTab: boolean) => {
  const isEdge = (): boolean =>
    navigator.userAgent.includes('Edg/') && !navigator.userAgent.includes('Chrome')

  // openInNewTab is always true
  const windowReference = !isEdge() && openInNewTab ? globalThis.open() : null

  const token = await getToken()
  // Use customer ppId if in helpdesk mode, otherwise use user's ppId from token
  const isHelpdesk = hasRole('helpdesk')
  let partnerProxyId: string | null = null

  if (isHelpdesk) {
    // Try to use store if Pinia is active (e.g., in Vue component context)
    // If Pinia is not active, throw error and cancel download
    try {
      const helpdeskStore = useHelpdeskStore()
      partnerProxyId = helpdeskStore.getCustomerPartnerProxyId()
      if (!partnerProxyId) {
        console.error(
          'Error: Customer partnerProxyId not available in helpdesk context. Please search for a customer first.'
        )
        return
      }
    } catch (error) {
      console.error(
        'Error: Pinia store not available. Cannot retrieve customer partnerProxyId in helpdesk mode.',
        error
      )
      return
    }
  } else {
    // For non-helpdesk users, use user's ppId from token
    partnerProxyId = getPartnerProxyId()
  }

  if (!partnerProxyId) {
    console.error('Error: partnerProxyId not available')
    return
  }

  const queryParams = new URLSearchParams({
    partnerProxyId: partnerProxyId
  }).toString()

  try {
    const response = await fetch(
      `${globalThis.ENV_CONFIG?.SERVICE_HOSTS.POSTBOX}/documents/${document.metadataId}/attachment?${queryParams}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          KeyId: globalThis.ENV_CONFIG?.API_KEY
        }
      }
    )

    if (response.ok) {
      const blobResponse = await response.blob()
      const blob = new Blob([blobResponse], { type: 'application/pdf' })

      const contentDispositionHeader = response.headers.get('content-disposition')
      const contentDisposition: string | null = contentDispositionHeader ?? ''
      const fileNameMatch: RegExpMatchArray | null = contentDisposition
        ? RegExp(/filename="(.+)"/).exec(contentDisposition)
        : null
      const fileName: string =
        fileNameMatch && fileNameMatch.length === 2 ? fileNameMatch[1]! : 'file.pdf'

      // NEU: Helpdesk + openInNewTab => forcePdfInNewTabIframe
      if (isHelpdesk && openInNewTab) {
        forcePdfInNewTabIframe(blob, fileName)
        return
      }

      const blobURL = globalThis.URL.createObjectURL(blob)

      if (windowReference) {
        // Chrome and others => open in new tab with globalThis.open() | windowReference = globalThis.open()
        windowReference.onload = () => {
          if (windowReference.location.href === blobURL) {
            windowReference.document.title = fileName
            globalThis.URL.revokeObjectURL(blobURL)
          }
        }
        windowReference.location.href = blobURL
      } else {
        // Edge => null | windowReference = null
        // Use msSaveOrOpenBlob if available (IE and Edge) to prompt download
        const navigatorIE = navigator as NavigatorIE
        if (navigatorIE.msSaveOrOpenBlob) {
          navigatorIE.msSaveOrOpenBlob(blob, fileName)
          return
        } else {
          const tempDownloadLink: HTMLAnchorElement =
            globalThis.document.createElement('a')
          tempDownloadLink.setAttribute('style', 'display: none;')
          tempDownloadLink.setAttribute('href', blobURL)
          tempDownloadLink.setAttribute('download', fileName)
          tempDownloadLink.onload = () => globalThis.URL.revokeObjectURL(blobURL)

          globalThis.document.body.appendChild(tempDownloadLink)
          tempDownloadLink.click()
          globalThis.document.body.removeChild(tempDownloadLink)
        }
      }
    } else {
      // Handle error response
    }
  } catch (_error) {
    // Handle fetch error
  }

  document.readStatus = 'read'
}
